import { AccordionProps as MuiAccordionProps } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ComponentItem } from "designSystem/types/component-item-types";
import {
	Accordion as MuiAccordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	OverflowText
} from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { OverflowTextProps } from "designSystem/components/text/OverflowText";
import { TooltipProps } from "designSystem/components/tooltip/Tooltip";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent } from "react";
import { CnlsAndValues, ComponentFilterItem } from "../common";
import FilterDropdownList, { FilterDropdownListProps } from "./FilterDropdownList";
import FilterStore from "./FilterStore";

const tooltipProps: Partial<TooltipProps> = { placement: "right" };
export const GroupTitle = (props: OverflowTextProps) => {
	return (
		<OverflowText
			bold
			color="textSecondary"
			size={10}
			tooltipProps={tooltipProps}
			uppercase
			{...props}
		/>
	);
};

interface AccordionDetailsSectionProps extends FilterDropdownListProps<ComponentFilterItem> {

}

const AccordionDetailsSection = (props: AccordionDetailsSectionProps) => {
	return (
		<AccordionDetails>
			<FilterDropdownList<ComponentFilterItem>
				showCount
				{...props}
			/>
		</AccordionDetails>
	);
};

interface AccordionProps extends Partial<MuiAccordionProps> {
	/** the item to display as the Accordion */
	item: ComponentItem;
}

const Accordion = (props: AccordionProps) => {
	const {
		item,
		children,
		...otherProps
	} = props;
	const { name } = item;

	return (
		<MuiAccordion
			{...otherProps}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				{name}
			</AccordionSummary>
			{children}
		</MuiAccordion>
	);
};

interface FilterGroupProps<T extends FilterStore> {
	/** store for component */
	store: T;
}

const FilterGroup: FunctionComponent<FilterGroupProps<FilterStore>> = observer((props) => {
	const loc = useLocale();
	const { store } = props;

	if (!store) {
		return null;
	}

	const {
		expandedItem,
		deselectValuesPerSpec,
		itemsMap,
		onExpand,
		resetFilterMap,
		setFilterSpec,
		setExpandedItem,
		specsMap,
		valueStoresBySpec,
	} = store;

	/** callback for expand */
	const handleChange = (item: ComponentItem, isExpanded: boolean) => {
		setExpandedItem(isExpanded ? item : null);

		if (isExpanded) {
			onExpand(item);
		}
	};

	/** apply the filter */
	const onFilter = (attributeItem: ComponentItem, isSelected: boolean, cnlsAndValues: CnlsAndValues) => {
		const spec = specsMap[attributeItem.id];
		setFilterSpec(spec, cnlsAndValues, isSelected);
	};

	// after reset the filter, reset the flag
	const postReset = (nodeTypeOfAttribute: string) => {
		store.setFilterReset(nodeTypeOfAttribute, false);
	};

	return (
		<Fragment>
			{Object.keys(itemsMap)
				.map((nodeType) => (
				/* We have multiple Accordions here, the Box of this level is necessary so that the styling can be applied accordingly */
					<Box key={nodeType}>
						<Box py={2}>
							<GroupTitle res="RuleFilterSection" resParams={[loc.type(nodeType)]} />
						</Box>
						{
							itemsMap[nodeType].map((item) => (
								<Accordion
									expanded={expandedItem?.id === item.id}
									item={item}
									key={item.id}
									onChange={(event, isExpanded) => handleChange(item, isExpanded)}
								>
									{valueStoresBySpec?.get(item.id)?.length > 0 && (
										<AccordionDetailsSection
											deselectIds={deselectValuesPerSpec.get(item.id)}
											items={valueStoresBySpec.get(item.id)}
											onChecked={(cnlAndValue, isSelected) => onFilter(item, isSelected, cnlAndValue)}
											postReset={() => postReset(nodeType)}
											reset={resetFilterMap.get(nodeType)}
										/>
									)}
								</Accordion>
							))
						}
					</Box>
				))
			}
		</Fragment>
	);
});

export default FilterGroup;